import React, { Component } from 'react';
import { Provider } from 'react-redux';
import { applyMiddleware } from 'helpers/redux_dev_tools';
import LocalStoreFactory from 'helpers/redux/local_store_factory';
import thunk from 'redux-thunk';
import PropTypes from 'prop-types';

import ProjectNavigator from './components/project_navigator';
import reducers from './reducers';
import { ProjectPropTypes } from 'components/project/prop_types';
import contactRolePropTypes from '../project/prop_types/contact_roles';
import { ActionIndicatorType } from 'bundles/project/prop_types/utils';
import { MediaPropTypes } from 'bundles/project/components/media_tab/media_props_types';

const LEADS_TAB_KEY = 'leadsTab';

export default class ProjectEditor extends Component {
  static propTypes = {
    accessCrm: PropTypes.bool,
    actionIndicators: PropTypes.objectOf(ActionIndicatorType),
    activityCsvVaultsPath: PropTypes.string,
    airbrake: PropTypes.shape({
      projectId: PropTypes.string,
      apiKey: PropTypes.string,
      environment: PropTypes.string
    }),
    apexDealStagesConfig: PropTypes.object,
    areaAnalyticsTabPath: PropTypes.string,
    availableDatabaseOptions: PropTypes.arrayOf(PropTypes.array),
    caDownloadPath: PropTypes.string,
    callLists: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired
    })),
    canDisplayEmailLink: PropTypes.bool.isRequired,
    canDuplicateProject: PropTypes.bool.isRequired,
    canShowDemographicsPoc: PropTypes.bool,
    canExportShadows: PropTypes.bool,
    clientReportActive: PropTypes.bool.isRequired,
    commercialEdgeLogoPath: PropTypes.string.isRequired,
    connectableObjects: PropTypes.array,
    contactRoles: contactRolePropTypes,
    currentUser: PropTypes.shape({
      callListsEnabled: PropTypes.bool
    }).isRequired,
    dashboardPath: PropTypes.string,
    databaseLinksAccess: PropTypes.bool.isRequired,
    dealFormProps: PropTypes.object,
    dealPaths: PropTypes.object,
    dealsTab: PropTypes.object,
    defaultTaskAssignee: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    }),
    demographicsOptions: PropTypes.object,
    demographicsTabPath: PropTypes.string,
    disabledTooltips: PropTypes.object,
    disableTaskAssociations: PropTypes.bool,
    docTabPath: PropTypes.string,
    documentCenter: PropTypes.bool,
    downloadPdfEnabled: PropTypes.bool,
    edgeCrm: PropTypes.object,
    emailCa: PropTypes.bool,
    emailTabPath: PropTypes.string,
    features: PropTypes.shape({
      backoffice: PropTypes.bool,
      pipeline: PropTypes.bool,
      projectAttachments: PropTypes.bool,
      projectCollaborators: PropTypes.bool,
      reminders: PropTypes.bool,
      shadowGridCoreModels: PropTypes.bool,
    }).isRequired,
    forUser: PropTypes.shape({
      id: PropTypes.number.isRequired,
      canDeleteAttachments: PropTypes.bool,
    }),
    helpCenterUrl: PropTypes.string,
    leadFieldFormat: PropTypes.object,
    leadReferralSourceFilterOptions: PropTypes.array,
    leadReferralSources: PropTypes.array,
    leadsPath: PropTypes.string,
    leadStatuses: PropTypes.array,
    loanProgramsSearchPath: PropTypes.string,
    mediaTabPath: PropTypes.string,
    mediaUiUpdate: PropTypes.bool,
    metricsZipPath: PropTypes.string,
    pageFlags: PropTypes.object,
    paths: PropTypes.object,
    plansTabPath: PropTypes.string,
    project: ProjectPropTypes.isRequired,
    propertyComps: PropTypes.object,
    royaltiesTabPath: PropTypes.string,
    shadowGridContext: PropTypes.object,
    shadowGridUrls: PropTypes.object,
    sharingTabPath: PropTypes.string,
    showClientReport: PropTypes.bool,
    showProjectConnections: PropTypes.bool,
    signsTabPath: PropTypes.string,
    syndicationTabPath: PropTypes.string,
    tasksCsvPath: PropTypes.string,
    users: PropTypes.array,
    vaultActivitiesPropertyPath: PropTypes.string,
    vaultActivitiesSvgPath: PropTypes.string,
    visualMediaData: MediaPropTypes.isRequired,
    vouchersSearchPath: PropTypes.string,
    vouchersTab: PropTypes.object,
    websiteTabPath: PropTypes.string
  };

  constructor(props) {
    super(props);

    const startingState = {
      actionIndicators: props.actionIndicators,
      availableDatabaseOptions: props.availableDatabaseOptions,
      airbrake: props.airbrake,
      apexDealStagesConfig: props.apexDealStagesConfig,
      connectableObjects: props.connectableObjects,
      contactRoles: props.contactRoles,
      currentUser: props.currentUser,
      dealsTab: props.dealsTab,
      docs: { nonMarketing: { fetching: true } },
      edgeCrm: props.edgeCrm,
      features: props.features,
      leads: {},
      leadsOptions: {
        selectOptions: {
          referralSources: props.leadReferralSources,
          statuses: props.leadStatuses
        }
      },
      mediaUiUpdate: props.mediaUiUpdate,
      pageFlags: props.pageFlags,
      project: props.project,
      propertyComps: props.propertyComps,
      shadowGridOptions: {
        canExportShadows: props.canExportShadows,
        context: props.shadowGridContext,
        urls: props.shadowGridUrls,
      },
      session: {
        currentColumns: null,
        currentCompanyColumns: null,
        currentPersonColumns: null
      },
      taskOptions: {
        hideContactSelect: this.props.disableTaskAssociations,
        hidePropertySelect: this.props.disableTaskAssociations
      },
      leadShow: {
        isCaUploading: false
      },
      tasks: { fetching: true },
      visualMediaData: this.props.visualMediaData,
      vouchersTab: props.vouchersTab
    };

    const storeFactory = new LocalStoreFactory(LEADS_TAB_KEY, { permit: ['session'] });
    this.store = storeFactory.store(reducers, startingState, applyMiddleware(thunk));

    if (module.hot) {
      module.hot.accept('./reducers/index', () => {
        this.store.replaceReducer(require('./reducers/index'));
      });
    }
  }

  render() {
    return (
      <Provider store={this.store}>
        <ProjectNavigator
          activityCsvVaultsPath={this.props.activityCsvVaultsPath}
          areaAnalyticsTabPath={this.props.areaAnalyticsTabPath}
          availableDatabaseOptions={this.props.availableDatabaseOptions}
          caDownloadPath={this.props.caDownloadPath}
          callLists={this.props.callLists}
          canDisplayEmailLink={this.props.canDisplayEmailLink}
          canDuplicateProject={this.props.canDuplicateProject}
          canShowDemographicsPoc={this.props.canShowDemographicsPoc}
          clientReportActive={this.props.clientReportActive}
          commercialEdgeLogoPath={this.props.commercialEdgeLogoPath}
          dashboardPath={this.props.dashboardPath}
          databaseLinksAccess={this.props.databaseLinksAccess}
          dealFormProps={this.props.dealFormProps}
          dealPaths={this.props.dealPaths}
          defaultTaskAssignee={this.props.defaultTaskAssignee}
          demographicsOptions={this.props.demographicsOptions}
          demographicsTabPath={this.props.demographicsTabPath}
          disabledTooltips={this.props.disabledTooltips}
          docTabPath={this.props.docTabPath}
          documentCenter={this.props.documentCenter}
          downloadPdfEnabled={this.props.downloadPdfEnabled}
          edgeCrm={this.props.edgeCrm}
          emailCa={this.props.emailCa}
          emailTabPath={this.props.emailTabPath}
          forUser={this.props.forUser}
          helpCenterUrl={this.props.helpCenterUrl}
          leadFieldFormat={this.props.leadFieldFormat}
          leadReferralSourceFilterOptions={this.props.leadReferralSourceFilterOptions}
          leadReferralSources={this.props.leadReferralSources}
          leadsPath={this.props.leadsPath}
          leadStatuses={this.props.leadStatuses}
          loanProgramsSearchPath={this.props.loanProgramsSearchPath}
          mediaTabPath={this.props.mediaTabPath}
          metricsZipPath={this.props.metricsZipPath}
          paths={this.props.paths}
          plansTabPath={this.props.plansTabPath}
          propertyComps={this.props.propertyComps}
          royaltiesTabPath={this.props.royaltiesTabPath}
          shadowGridPath={this.props.shadowGridUrls && this.props.shadowGridUrls.index}
          sharingTabPath={this.props.sharingTabPath}
          showClientReport={this.props.showClientReport}
          showProjectConnections={this.props.showProjectConnections}
          signsTabPath={this.props.signsTabPath}
          syndicationTabPath={this.props.syndicationTabPath}
          tasksCsvPath={this.props.tasksCsvPath}
          users={this.props.users}
          vaultActivitiesPropertyPath={this.props.vaultActivitiesPropertyPath}
          vaultActivitiesSvgPath={this.props.vaultActivitiesSvgPath}
          vouchersSearchPath={this.props.vouchersSearchPath}
          websiteTabPath={this.props.websiteTabPath}
        />
      </Provider>
    );
  }
}
